import {
  Button as AriaButton,
  Select as AriaSelect,
  FieldError,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  SelectValue,
  Text,
  type SelectProps as AriaSelectProps,
} from 'react-aria-components';

import type { FieldWidth } from '../TextField/TextField.tsx';
import styles from './Select.module.css';

export interface SelectOption {
  id: string;
  label: string;
}

/**
 * Single choice only. Use `value` / `defaultValue` / `onChange`; React Aria's older
 * `selectedKey` props are deprecated and left out so there is one way to do it.
 */
export interface SelectProps extends Omit<
  AriaSelectProps<SelectOption>,
  | 'children'
  | 'className'
  | 'style'
  | 'items'
  | 'selectionMode'
  | 'selectedKey'
  | 'defaultSelectedKey'
  | 'onSelectionChange'
> {
  /** Always visible, above the control. Never use the placeholder as the label. */
  label: string;
  /**
   * A short, fixed set of choices: a facility level, a status. Clinical choices —
   * patients, drugs, diagnoses, tests — use a type-to-search picker instead
   * (DESIGN.md §7), never a dropdown.
   */
  options: readonly SelectOption[];
  /** Helper text below the control. */
  description?: string;
  /** What is wrong and how to fix it. Setting it marks the field invalid. */
  errorMessage?: string;
  placeholder?: string;
  dense?: boolean;
  /** Width by expected content; unset fills the form width. */
  width?: FieldWidth;
}

export function Select({
  label,
  options,
  description,
  errorMessage,
  placeholder,
  dense = false,
  width,
  isInvalid,
  ...rest
}: SelectProps) {
  return (
    <AriaSelect
      {...rest}
      isInvalid={isInvalid === true || errorMessage !== undefined}
      className={styles.field}
      data-width={width}
      data-dense={dense || undefined}
    >
      <Label className={styles.label}>{label}</Label>
      <AriaButton className={styles.trigger}>
        <SelectValue className={styles.value}>
          {({ selectedText, isPlaceholder }) =>
            isPlaceholder ? (placeholder ?? 'Choose one') : selectedText
          }
        </SelectValue>
        <span className={styles.caret} aria-hidden="true" />
      </AriaButton>
      <FieldError className={styles.error}>{errorMessage}</FieldError>
      {description === undefined ? null : (
        <Text slot="description" className={styles.description}>
          {description}
        </Text>
      )}
      <Popover className={styles.popover}>
        <ListBox className={styles.list} items={options}>
          {(option) => (
            <ListBoxItem id={option.id} className={styles.item} textValue={option.label}>
              {option.label}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </AriaSelect>
  );
}
